/**
 * Offline Expense Service
 * 
 * Manages expense tracking when offline.
 * Stores expenses locally and queues them for sync when online.
 */

import { offlineStorage, tripStorage } from './offlineStorage';
import { syncQueue, type SyncAction } from './syncQueue';
import { networkMonitor } from './networkMonitor';
import { offlineManager } from './offlineManager';
import type { Expense, ExpenseCategory, PaymentMethod, ExpenseSplit } from './expenses';

const OFFLINE_EXPENSES_KEY = '@tripflow_offline_expenses';
/** Firestore expense IDs queued for deletion while offline — hide from merged lists until delete syncs. */
const PENDING_FIRESTORE_EXPENSE_DELETES_KEY = '@tripflow_pending_firestore_expense_deletes';
const MAX_PENDING_OFFLINE_EXPENSES_PER_TRIP = 250;

type PendingFirestoreExpenseDeletes = Record<string, string[]>;

export type OfflineExpense = Expense & {
  syncStatus: 'pending' | 'syncing' | 'synced' | 'failed';
  offlineId: string; // Temporary ID for offline expenses
  syncedId?: string; // Firestore ID after sync
  syncError?: string;
  queuedAt: number;
};

type OfflineExpensesCache = {
  [tripId: string]: {
    [expenseId: string]: OfflineExpense;
  };
};

const isFinitePositiveAmount = (value: unknown): boolean => {
  if (typeof value === 'number') return Number.isFinite(value) && value > 0;
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0;
  }
  return false;
};

const isValidDateLike = (value: unknown): boolean => {
  if (value instanceof Date) return Number.isFinite(value.getTime());
  if (typeof value === 'string' || typeof value === 'number') {
    const parsed = new Date(value);
    return Number.isFinite(parsed.getTime());
  }
  return false;
};

const normalizeCurrency = (value: unknown): string => {
  if (typeof value !== 'string') return '';
  const trimmed = value.trim().toUpperCase();
  return /^[A-Z]{3}$/.test(trimmed) ? trimmed : '';
};

const normalizeExpenseAmount = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const sanitizeTripExpensesCache = (
  tripExpenses: Record<string, OfflineExpense>,
): { sanitized: Record<string, OfflineExpense>; changed: boolean } => {
  const validEntries: Array<[string, OfflineExpense]> = [];
  let changed = false;

  for (const [key, expense] of Object.entries(tripExpenses)) {
    const amount = normalizeExpenseAmount((expense as unknown as Record<string, unknown>).amount);
    const currency = normalizeCurrency((expense as unknown as Record<string, unknown>).currency);
    const hasValidDate = isValidDateLike((expense as unknown as Record<string, unknown>).date);
    const isSynced = expense.syncStatus === 'synced';
    const hasPositiveAmount = isFinitePositiveAmount(amount);

    // Self-heal legacy poison rows:
    // - synced snapshots are no longer needed for rendering/merge
    // - invalid amount/currency/date rows should never be replayed into UI/sync
    if (isSynced || !hasPositiveAmount || !currency || !hasValidDate) {
      changed = true;
      continue;
    }

    const normalizedExpense: OfflineExpense = {
      ...expense,
      amount: amount as number,
      currency,
    };
    validEntries.push([key, normalizedExpense]);
  }

  // Keep only the newest pending/failed rows to avoid pathological local bloat.
  if (validEntries.length > MAX_PENDING_OFFLINE_EXPENSES_PER_TRIP) {
    changed = true;
    validEntries.sort((a, b) => (b[1].queuedAt || 0) - (a[1].queuedAt || 0));
  }

  const limitedEntries = validEntries.slice(0, MAX_PENDING_OFFLINE_EXPENSES_PER_TRIP);
  const sanitized = Object.fromEntries(limitedEntries);
  return { sanitized, changed };
};

export async function addPendingFirestoreExpenseDelete(tripId: string, expenseId: string): Promise<void> {
  const stored =
    (await offlineStorage.get<PendingFirestoreExpenseDeletes>(PENDING_FIRESTORE_EXPENSE_DELETES_KEY)) || {};
  const set = new Set(stored[tripId] || []);
  set.add(expenseId);
  stored[tripId] = [...set];
  await offlineStorage.set(PENDING_FIRESTORE_EXPENSE_DELETES_KEY, stored);
}

export async function removePendingFirestoreExpenseDelete(tripId: string, expenseId: string): Promise<void> {
  const stored =
    (await offlineStorage.get<PendingFirestoreExpenseDeletes>(PENDING_FIRESTORE_EXPENSE_DELETES_KEY)) || {};
  const next = (stored[tripId] || []).filter((id) => id !== expenseId);
  if (next.length === 0) {
    delete stored[tripId];
  } else {
    stored[tripId] = next;
  }
  await offlineStorage.set(PENDING_FIRESTORE_EXPENSE_DELETES_KEY, stored);
}

export async function getPendingFirestoreExpenseDeleteSet(tripId: string): Promise<Set<string>> {
  const stored =
    (await offlineStorage.get<PendingFirestoreExpenseDeletes>(PENDING_FIRESTORE_EXPENSE_DELETES_KEY)) || {};
  return new Set(stored[tripId] || []);
}

/** Rows are keyed by offline_* id; after sync, `expense.id` is Firestore id but the map key may stay offline_*. */
function findOfflineExpenseInTrip(
  tripExpenses: Record<string, OfflineExpense>,
  expenseId: string,
): { key: string; expense: OfflineExpense } | null {
  const direct = tripExpenses[expenseId];
  if (direct) return { key: expenseId, expense: direct };
  for (const [key, exp] of Object.entries(tripExpenses)) {
    if (exp.id === expenseId || exp.syncedId === expenseId || exp.offlineId === expenseId) {
      return { key, expense: exp };
    }
  }
  return null;
}

/** Seed cache when falling back to offline update for a Firestore-only expense. */
export async function seedFirestoreExpenseForOfflineUpdate(tripId: string, expense: Expense): Promise<void> {
  try {
    const stored = await offlineStorage.get<OfflineExpensesCache>(OFFLINE_EXPENSES_KEY) || {};
    const tripExpenses = stored[tripId] || {};
    if (findOfflineExpenseInTrip(tripExpenses, expense.id)) return;

    const now = new Date();
    const offlineExpense: OfflineExpense = {
      ...expense,
      tripId,
      createdAt: expense.createdAt ?? now,
      updatedAt: expense.updatedAt ?? now,
      syncStatus: 'synced',
      offlineId: expense.id,
      syncedId: expense.id,
      queuedAt: Date.now(),
    };
    tripExpenses[expense.id] = offlineExpense;
    stored[tripId] = tripExpenses;
    await offlineStorage.set(OFFLINE_EXPENSES_KEY, stored);
    console.log(`[OfflineExpenses] Seeded offline cache for Firestore expense ${expense.id}`);
  } catch (error) {
    console.error('[OfflineExpenses] Error seeding expense for offline update:', error);
    throw error;
  }
}

/**
 * Generate a temporary ID for offline expenses
 */
function generateOfflineId(): string {
  return `offline_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Add expense offline (stores locally and queues for sync)
 */
export async function addOfflineExpense(
  tripId: string,
  expense: Omit<Expense, 'id' | 'tripId' | 'createdAt' | 'updatedAt'>
): Promise<string> {
  try {
    const offlineId = generateOfflineId();
    const now = new Date();

    const offlineExpense: OfflineExpense = {
      ...expense,
      id: offlineId,
      tripId,
      createdAt: now,
      updatedAt: now,
      syncStatus: networkMonitor.isOnline() ? 'pending' : 'pending',
      offlineId,
      queuedAt: Date.now(),
    };

    // Store in local cache
    const stored = await offlineStorage.get<OfflineExpensesCache>(OFFLINE_EXPENSES_KEY) || {};
    if (!stored[tripId]) {
      stored[tripId] = {};
    }
    stored[tripId][offlineId] = offlineExpense;
    await offlineStorage.set(OFFLINE_EXPENSES_KEY, stored);

    // Queue for sync
    await syncQueue.queueAction({
      type: 'create_expense',
      payload: {
        tripId,
        expense: {
          amount: expense.amount,
          currency: expense.currency,
          category: expense.category,
          description: expense.description,
          date: expense.date.toISOString(),
          location: expense.location,
          merchant: expense.merchant,
          paymentMethod: expense.paymentMethod,
          receiptUrl: expense.receiptUrl,
          source: (expense as any).source,
          createdBy: expense.createdBy,
          split: expense.split,
        },
        offlineId,
      },
      tripId,
      userId: expense.createdBy,
    });

    console.log(`[OfflineExpenses] Added offline expense: ${offlineId} for trip ${tripId}`);
    return offlineId;
  } catch (error) {
    console.error('[OfflineExpenses] Error adding offline expense:', error);
    throw error;
  }
}

/**
 * Update expense offline
 */
export async function updateOfflineExpense(
  tripId: string,
  expenseId: string,
  updates: Partial<Omit<Expense, 'id' | 'tripId' | 'createdAt' | 'createdBy'>>
): Promise<void> {
  try {
    const stored = await offlineStorage.get<OfflineExpensesCache>(OFFLINE_EXPENSES_KEY) || {};
    const tripExpenses = stored[tripId] || {};
    const resolved = findOfflineExpenseInTrip(tripExpenses, expenseId);

    if (!resolved) {
      throw new Error(`Expense ${expenseId} not found in offline cache`);
    }

    const { key: storageKey, expense } = resolved;

    const updatedExpense: OfflineExpense = {
      ...expense,
      ...updates,
      updatedAt: new Date(),
      syncStatus: expense.syncedId ? 'pending' : expense.syncStatus, // If already synced, mark as pending for update
    };

    tripExpenses[storageKey] = updatedExpense;
    stored[tripId] = tripExpenses;
    await offlineStorage.set(OFFLINE_EXPENSES_KEY, stored);

    // Queue update action
    if (expense.syncedId) {
      // Update existing expense
      await syncQueue.queueAction({
        type: 'update_expense',
        payload: {
          tripId,
          expenseId: expense.syncedId,
          updates: {
            amount: updatedExpense.amount,
            currency: updatedExpense.currency,
            category: updatedExpense.category,
            description: updatedExpense.description,
            date: updatedExpense.date.toISOString(),
            location: updatedExpense.location,
            merchant: updatedExpense.merchant,
            paymentMethod: updatedExpense.paymentMethod,
            receiptUrl: updatedExpense.receiptUrl,
            source: (updatedExpense as any).source,
            split: updatedExpense.split,
          },
        },
        tripId,
        userId: expense.createdBy,
      });
    } else {
      // Re-queue create action with updated data
      await syncQueue.queueAction({
        type: 'create_expense',
        payload: {
          tripId,
          expense: {
            amount: updatedExpense.amount,
            currency: updatedExpense.currency,
            category: updatedExpense.category,
            description: updatedExpense.description,
            date: updatedExpense.date.toISOString(),
            location: updatedExpense.location,
            merchant: updatedExpense.merchant,
            paymentMethod: updatedExpense.paymentMethod,
            receiptUrl: updatedExpense.receiptUrl,
            source: (updatedExpense as any).source,
            createdBy: updatedExpense.createdBy,
            split: updatedExpense.split,
          },
          offlineId: expense.offlineId,
        },
        tripId,
        userId: expense.createdBy,
      });
    }

    console.log(`[OfflineExpenses] Updated offline expense: ${storageKey}`);
  } catch (error) {
    console.error('[OfflineExpenses] Error updating offline expense:', error);
    throw error;
  }
}

/**
 * Delete expense offline
 */
export async function deleteOfflineExpense(
  tripId: string,
  expenseId: string
): Promise<void> {
  try {
    const stored = await offlineStorage.get<OfflineExpensesCache>(OFFLINE_EXPENSES_KEY) || {};
    const tripExpenses = stored[tripId] || {};
    const resolved = findOfflineExpenseInTrip(tripExpenses, expenseId);

    if (!resolved) {
      return; // Already deleted or doesn't exist
    }

    const { key: storageKey, expense } = resolved;

    // Remove from cache
    delete tripExpenses[storageKey];
    stored[tripId] = tripExpenses;
    await offlineStorage.set(OFFLINE_EXPENSES_KEY, stored);

    // Never synced — drop any queued creates for this offline row so sync cannot resurrect it.
    if (!expense.syncedId) {
      await syncQueue.removeActionsWhere(
        (a) =>
          a.type === 'create_expense' &&
          a.tripId === tripId &&
          (a.payload as { offlineId?: string }).offlineId === expense.offlineId,
      );
    }

    // Queue delete action if expense was synced
    if (expense.syncedId) {
      await syncQueue.queueAction({
        type: 'delete_expense',
        payload: {
          tripId,
          expenseId: expense.syncedId,
        },
        tripId,
        userId: expense.createdBy,
      });
    }

    console.log(`[OfflineExpenses] Deleted offline expense: ${storageKey}`);
  } catch (error) {
    console.error('[OfflineExpenses] Error deleting offline expense:', error);
    throw error;
  }
}

/**
 * Get offline expenses for a trip
 */
export async function getOfflineExpenses(tripId: string): Promise<OfflineExpense[]> {
  try {
    const stored = await offlineStorage.get<OfflineExpensesCache>(OFFLINE_EXPENSES_KEY) || {};
    const tripExpenses = stored[tripId] || {};
    const { sanitized, changed } = sanitizeTripExpensesCache(tripExpenses);

    if (changed) {
      stored[tripId] = sanitized;
      await offlineStorage.set(OFFLINE_EXPENSES_KEY, stored);
      console.warn(
        `[OfflineExpenses] Sanitized local cache for trip ${tripId} (kept ${Object.keys(sanitized).length} entries)`,
      );
    }

    return Object.values(sanitized);
  } catch (error) {
    console.error('[OfflineExpenses] Error getting offline expenses:', error);
    return [];
  }
}

/**
 * Get all pending expenses (across all trips)
 */
export async function getPendingExpenses(): Promise<OfflineExpense[]> {
  try {
    const stored = await offlineStorage.get<OfflineExpensesCache>(OFFLINE_EXPENSES_KEY) || {};
    const allExpenses: OfflineExpense[] = [];

    for (const tripExpenses of Object.values(stored)) {
      for (const expense of Object.values(tripExpenses)) {
        if (expense.syncStatus === 'pending' || expense.syncStatus === 'failed') {
          allExpenses.push(expense);
        }
      }
    }

    return allExpenses;
  } catch (error) {
    console.error('[OfflineExpenses] Error getting pending expenses:', error);
    return [];
  }
}

/**
 * Mark expense as synced
 */
export async function markExpenseSynced(
  tripId: string,
  offlineId: string,
  syncedId: string
): Promise<void> {
  try {
    const stored = await offlineStorage.get<OfflineExpensesCache>(OFFLINE_EXPENSES_KEY) || {};
    const tripExpenses = stored[tripId] || {};
    const expense = tripExpenses[offlineId];

    if (expense) {
      expense.syncedId = syncedId;
      expense.syncStatus = 'synced';
      expense.id = syncedId; // Update ID to synced ID
      
      tripExpenses[offlineId] = expense;
      stored[tripId] = tripExpenses;
      await offlineStorage.set(OFFLINE_EXPENSES_KEY, stored);

      console.log(`[OfflineExpenses] Marked expense ${offlineId} as synced: ${syncedId}`);
    }
  } catch (error) {
    console.error('[OfflineExpenses] Error marking expense as synced:', error);
  }
}

/**
 * Mark expense sync as failed
 */
export async function markExpenseSyncFailed(
  tripId: string,
  offlineId: string,
  error: string
): Promise<void> {
  try {
    const stored = await offlineStorage.get<OfflineExpensesCache>(OFFLINE_EXPENSES_KEY) || {};
    const tripExpenses = stored[tripId] || {};
    const expense = tripExpenses[offlineId];

    if (expense) {
      expense.syncStatus = 'failed';
      expense.syncError = error;
      
      tripExpenses[offlineId] = expense;
      stored[tripId] = tripExpenses;
      await offlineStorage.set(OFFLINE_EXPENSES_KEY, stored);

      console.log(`[OfflineExpenses] Marked expense ${offlineId} as failed: ${error}`);
    }
  } catch (error) {
    console.error('[OfflineExpenses] Error marking expense sync as failed:', error);
  }
}

/**
 * Clear synced expenses from cache (keep only pending ones)
 */
export async function clearSyncedExpenses(tripId?: string): Promise<void> {
  try {
    const stored = await offlineStorage.get<OfflineExpensesCache>(OFFLINE_EXPENSES_KEY) || {};
    
    if (tripId) {
      // Clear synced expenses for specific trip
      const tripExpenses = stored[tripId] || {};
      const pendingExpenses: Record<string, OfflineExpense> = {};
      
      for (const [id, expense] of Object.entries(tripExpenses)) {
        if (expense.syncStatus !== 'synced') {
          pendingExpenses[id] = expense;
        }
      }
      
      stored[tripId] = pendingExpenses;
    } else {
      // Clear all synced expenses
      for (const tripIdKey of Object.keys(stored)) {
        const tripExpenses = stored[tripIdKey] || {};
        const pendingExpenses: Record<string, OfflineExpense> = {};
        
        for (const [id, expense] of Object.entries(tripExpenses)) {
          if (expense.syncStatus !== 'synced') {
            pendingExpenses[id] = expense;
          }
        }
        
        stored[tripIdKey] = pendingExpenses;
      }
    }
    
    await offlineStorage.set(OFFLINE_EXPENSES_KEY, stored);
    console.log(`[OfflineExpenses] Cleared synced expenses${tripId ? ` for trip ${tripId}` : ''}`);
  } catch (error) {
    console.error('[OfflineExpenses] Error clearing synced expenses:', error);
  }
}

/**
 * Get count of pending expenses for a trip
 */
export async function getPendingExpenseCount(tripId: string): Promise<number> {
  try {
    const expenses = await getOfflineExpenses(tripId);
    return expenses.filter(e => e.syncStatus === 'pending' || e.syncStatus === 'failed').length;
  } catch (error) {
    console.error('[OfflineExpenses] Error getting pending expense count:', error);
    return 0;
  }
}

